import { db } from "./dbClient";

// ─── AuditLogPanel.jsx ────────────────────────────────────────────────────────
// Reusable activity log panel. Drop into any page.
// Import as: import AuditLogPanel from "./AuditLogPanel";
//
// Usage:
//   // All activity for a module:
//   <AuditLogPanel company={company} module="transaction_monitoring" />
//   // Activity for one entity (e.g. a specific case):
//   <AuditLogPanel company={company} module="cases" entityId={caseData.id} />

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { SkeletonTable } from "./Skeleton";
import { downloadCSV, printToPDF } from "./exportUtils";

function fmtVal(val) {
  if (!val) return null;
  if (typeof val === "object") {
    return Object.entries(val).map(([k, v]) => `${k}: ${v}`).join(", ");
  }
  return String(val);
}

export default function AuditLogPanel({ company, module: mod = null, entityId = null, limit = 100 }) {
  const C = useTheme();
  const { t } = useTranslation();
  const MODULE_COLOR = {
    cases:                  C.danger,
    transaction_monitoring: "#F0B429",
    kyc:                    C.accent,
    sar:                    C.purple,
    settings:               C.textMid,
    team:                   C.success,
    onboarding:             "#A78BFA",
  };
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo,   setAppliedTo]   = useState("");

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    let q = db.from("audit_logs").select("*")
      .eq("company_id", company.id)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (mod)          q = q.eq("module", mod).execute();
    if (entityId)     q = q.eq("entity_id", entityId).execute();
    if (appliedFrom)  q = q.gte("created_at", appliedFrom);
    if (appliedTo)    q = q.lte("created_at", appliedTo + "T23:59:59");
    q.then(({ data }) => { setLogs(data || []); setLoading(false); });
  }, [company?.id, mod, entityId, limit, appliedFrom, appliedTo]);

  function applyDateRange() { setAppliedFrom(dateFrom); setAppliedTo(dateTo); }
  function clearDateRange()  { setDateFrom(""); setDateTo(""); setAppliedFrom(""); setAppliedTo(""); }

  function handleExportCSV() {
    downloadCSV(logs.map(l => ({
      date:        new Date(l.created_at).toLocaleString(),
      user:        l.user_name || l.user_email || "",
      action:      l.action,
      module:      l.module,
      entity:      l.entity_name || l.entity_id || "",
      old_value:   l.old_value ? JSON.stringify(l.old_value) : "",
      new_value:   l.new_value ? JSON.stringify(l.new_value) : "",
    })), `crossregguard_audit_${mod || "all"}${appliedFrom ? `_${appliedFrom}` : ""}${appliedTo ? `_to_${appliedTo}` : ""}_${Date.now()}.csv`);
  }

  function handleExportPDF() {
    const rows = logs.map(l => `
      <tr>
        <td>${new Date(l.created_at).toLocaleString()}</td>
        <td>${l.user_name || l.user_email || "—"}</td>
        <td>${l.action?.replace(/_/g, " ") || "—"}</td>
        <td>${l.module || "—"}</td>
        <td>${l.entity_name || l.entity_id || "—"}</td>
        <td>${l.old_value ? JSON.stringify(l.old_value) : "—"}</td>
        <td>${l.new_value ? JSON.stringify(l.new_value) : "—"}</td>
      </tr>`).join("");
    const dateRangeTitle = appliedFrom && appliedTo
      ? ` — ${appliedFrom} to ${appliedTo}`
      : appliedFrom ? ` — from ${appliedFrom}`
      : appliedTo ? ` — to ${appliedTo}`
      : "";
    printToPDF(`Audit Log — ${mod || "All Modules"}${dateRangeTitle}`, `
      <div class="section">
        <table>
          <thead><tr><th>Date</th><th>User</th><th>Action</th><th>Module</th><th>Entity</th><th>Before</th><th>After</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`);
  }

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: 20, marginTop: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 16 }}>
        <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>
          {t('auditLog.activityLog')}
          {logs.length > 0 && (
            <span style={{ color: C.textDim, fontSize: 11, fontWeight: 400,
              marginLeft: 8 }}>{logs.length} {t('auditLog.entries')}</span>
          )}
        </div>
        {logs.length > 0 && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleExportCSV}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 6, color: C.textMid, padding: "5px 12px",
                cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
              {t('common.exportCSV')}
            </button>
            <button onClick={handleExportPDF}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 6, color: C.textMid, padding: "5px 12px",
                cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
              {t('common.exportPDF')}
            </button>
          </div>
        )}
      </div>

      {/* Date range filter */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>From:</span>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); if (dateTo && e.target.value > dateTo) setDateTo(e.target.value); }}
          style={{ background: C.bg, border: `1px solid ${dateFrom ? C.accent : C.border}`,
            borderRadius: 6, color: C.text, padding: "5px 8px",
            fontFamily: "'DM Mono',monospace", fontSize: 11, outline: "none",
            colorScheme: "dark" }} />
        <span style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>To:</span>
        <input type="date" value={dateTo} onChange={e => { if (!dateFrom || e.target.value >= dateFrom) setDateTo(e.target.value); }}
          style={{ background: C.bg, border: `1px solid ${dateTo ? C.accent : C.border}`,
            borderRadius: 6, color: C.text, padding: "5px 8px",
            fontFamily: "'DM Mono',monospace", fontSize: 11, outline: "none",
            colorScheme: "dark" }} />
        <button onClick={applyDateRange} disabled={!dateFrom && !dateTo}
          style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11,
            background: C.accent, border: "none", color: C.bg,
            fontFamily: "'DM Mono',monospace", cursor: (!dateFrom && !dateTo) ? "default" : "pointer",
            opacity: (!dateFrom && !dateTo) ? 0.4 : 1 }}>
          Apply
        </button>
        {(appliedFrom || appliedTo) && (
          <button onClick={clearDateRange}
            style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11,
              background: "transparent", border: `1px solid ${C.border}`,
              color: C.textDim, fontFamily: "'DM Mono',monospace", cursor: "pointer" }}>
            Clear
          </button>
        )}
        {(appliedFrom || appliedTo) && (
          <span style={{ color: C.accent, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
            {appliedFrom || "…"} → {appliedTo || "…"}
          </span>
        )}
      </div>

      {loading ? (
        <SkeletonTable rows={4} cols={4} />
      ) : logs.length === 0 ? (
        <div style={{ color: C.textDim, fontSize: 12, padding: "24px 0",
          textAlign: "center" }}>
          {t('auditLog.noActivity')}
        </div>
      ) : (
        <div>
          {logs.map(log => (
            <div key={log.id} style={{ display: "flex", gap: 12, padding: "10px 0",
              borderBottom: `1px solid ${C.border}`, alignItems: "flex-start" }}>
              {/* Color dot by module */}
              <div style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 6,
                flexShrink: 0, background: MODULE_COLOR[log.module] || C.textDim }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>
                      {log.user_name || log.user_email || "System"}
                    </span>
                    <span style={{ color: C.textDim, fontSize: 12 }}>
                      {" — "}{log.action?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <span style={{ color: C.textDim, fontSize: 10, flexShrink: 0 }}>
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>
                {log.entity_name && (
                  <div style={{ color: C.textMid, fontSize: 11, marginTop: 2 }}>
                    {log.entity_name}
                  </div>
                )}
                {(log.old_value || log.new_value) && (
                  <div style={{ marginTop: 5, display: "flex", gap: 12,
                    flexWrap: "wrap", fontSize: 11 }}>
                    {log.old_value && (
                      <span style={{ color: C.danger, background: `${C.danger}12`,
                        padding: "2px 7px", borderRadius: 4 }}>
                        {t('auditLog.before')}: {fmtVal(log.old_value)}
                      </span>
                    )}
                    {log.new_value && (
                      <span style={{ color: C.success, background: `${C.success}12`,
                        padding: "2px 7px", borderRadius: 4 }}>
                        {t('auditLog.after')}: {fmtVal(log.new_value)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}